import axios from "axios"
import accessToken from "./jwt-token-access/auth-token-header"
import { getConfiguration } from './backend_helper'
import { Alert, ToastHelper } from './alerts/alertHelper';

/**
 * Lectura tolerante de `authUser` en localStorage.
 *
 * El JSON.parse crudo lanzaba SyntaxError ante storage corrupto o truncado, y
 * eso ocurre durante el cuerpo de App.js (antes del routing), dejando la app
 * inutilizable sin devtools. El removeItem del catch es deliberado: degrada a
 * "sin sesion", que Authmiddleware ya redirige a /login.
 */
export function readAuthUser() {
    try {
        const raw = localStorage.getItem("authUser")
        if (!raw) return {}
        const parsed = JSON.parse(raw)
        return parsed && typeof parsed === 'object' ? parsed : {}
    } catch (e) {
        console.error('authUser corrupto en localStorage', e)
        localStorage.removeItem("authUser")   // auto-sanado: la siguiente carga es un login limpio
        return {}
    }
}

export function getStorageUser() {
    return readAuthUser().user ?? {};
}

export function getStorageConfig() {
    return readAuthUser().configuration ?? { logo: {}, menu: {}, header: {} };
}

export function getStorageCompany() {
    return readAuthUser().company ?? {};
}

export function getCompaniesStorage() {
    return readAuthUser().companies ?? [];
}

export function getPolicyUser() {
    // `'user' in obj` era cierto aun con obj.user === null, y obj.user.can truena.
    return readAuthUser().user?.can ?? { configurations: false, users: false };
}
const API_URL = process.env.REACT_APP_BACKEND_API_URL
const axiosApi = axios.create({
    baseURL: API_URL
})
function addHeaderAuthorization() {
    const auth = accessToken()
    if ('Authorization' in auth)
        axiosApi.defaults.headers.common["Authorization"] = 'Bearer ' + auth.Authorization
}

/**
 * Sentinel de fallo.
 *
 * Antes get/put/del resolvian a `undefined` ante cualquier error de red, y las
 * ~120 pantallas que hacen `response.data` justo despues truenaban con
 * TypeError (pantalla en blanco, porque no habia ErrorBoundary). Devolver un
 * objeto con las claves dominantes hace que `response.status` (falsy => rama de
 * error), `response.data.map(...)`, `response.data.data` y `response.meta`
 * sean todos seguros sin tocar los call sites.
 *
 * El toast de error ya lo dispara errors(), que corre ANTES de este return.
 */
const failure = (extra = {}) => ({ status: false, data: [], meta: {}, message: '', ...extra })

/** Errores de validacion 422 como objeto crudo (getError los devuelve unidos como string). */
const extractErrors = err => err?.response?.data?.errors ?? {}

export async function get(url, config = {}) {
    addHeaderAuthorization()
    return await axiosApi
        .get(url, { withToast: false, ...config })
        .then(response => {
            if (response?.data?.message) {
                if (config.withToast == true)
                    ToastHelper(response.data.message, response.data.status ? 'success' : 'error')
            }
            return response.data
        }
        )
        .catch(err => {
            if (axios.isCancel(err)) {
                throw new Error('Request canceled');
            } else {
                errors(err)
                return failure()
            }
        })
}

export async function post(url, data, config = {}) {
    addHeaderAuthorization()
    return axiosApi
        .post(url, { ...data }, { ...config })
        .then(response => {
            console.log(response)

            if (response?.message) {
                ToastHelper(response.message, response?.status ? 'success' : 'error')
            } else {
                if (response?.data?.message) {
                    ToastHelper(response.data.message, response.data.status ? 'success' : 'error')
                } else {
                    ToastHelper("Completado", 'success')
                }
            }
            return response.data
        }).catch(err => {
            errors(err)
            const errs = extractErrors(err)
            // COMPAT: 23 pantallas leen `response.response.data.errors` (todas las
            // AddUpdModal de Catalogs/Operaciones/Reports/Profiles + FilesListHelper.js).
            // Antes post() devolvia `false`, asi que `false.response` era undefined y
            // `.data` truenaba: la ruta de errores 422 nunca habia funcionado.
            // No "limpiar" esta forma anidada sin migrar esos 23 archivos a `response.errors`.
            return failure({ errors: errs, response: { data: { errors: errs } } })
        })
}

/**
 * POST silencioso, sin toasts y sin efectos secundarios.
 *
 * El reporte de un crash NO puede pasar por post(): post() dispara ToastHelper
 * tanto en exito como en error, y su handler errors() ante un 401 borra la
 * sesion y hace window.location.replace('/login') (ver mas abajo). Reportar un
 * error expulsaria al usuario de la aplicacion.
 *
 * Nunca lanza: ante cualquier fallo resuelve al sentinel failure().
 */
export async function postQuiet(url, data, config = {}) {
    try {
        addHeaderAuthorization()
        return await axiosApi
            .post(url, { ...data }, { ...config })
            .then(response => response?.data ?? failure())
            .catch(() => failure())
    } catch (e) {
        return failure()
    }
}

// TODO: postData se traga los errores (catch vacio) y devuelve undefined.
// No se toca aqui porque cambiaria el comportamiento de los flujos de subida
// de archivos, que no hay forma de probar automaticamente.
export async function postData(url, data, config = {}) {
    addHeaderAuthorization()
    return axiosApi
        .post(url, data, { ...config })
        .then(response => response.data)
        .catch(err => {
            //errors(err)
            //return err
        })
}

export async function file(url, config = {}) {
    addHeaderAuthorization()
    return await axiosApi
        .get(url, { ...config })
        .then(response => response.data)
        .catch(err => {
            errors(err)
        })
}

export async function put(url, data, config = {}) {
    addHeaderAuthorization()
    return axiosApi
        .put(url, { ...data }, { ...config })
        .then(response => {
            if (response?.data?.message) {
                ToastHelper(response.data.message, response.data.status ? 'success' : 'error')
            }
            if (response?.message) {
                ToastHelper(response.message, response?.status ? 'success' : 'error')
            }
            return response.data
        })
        .catch(err => {
            errors(err)
            return failure()
        })
}

export async function del(url, config = {}) {
    addHeaderAuthorization()
    return await axiosApi
        .delete(url, { ...config })
        .then(response => {
            if (response?.data?.message) {
                ToastHelper(response.data.message, response.data.status ? 'success' : 'error')
            }
            return response.data
        }).catch(err => {
            errors(err)
            return failure()
        })
}

export function postFile(url, data, config = {}) {
    console.log(data.file)
    const formData = new FormData();
    formData.append('file', data.file);
    addHeaderAuthorization();
    return axiosApi
        .post(url, { formData, data }, { ...config })
        .then(response => response.data)
        .catch(err => {
            errors(err)
        })
}

export function postFile1(url, file, config = {}) {
    const formData = new FormData();
    formData.append('file', file);
    addHeaderAuthorization();
    return axiosApi
        .post(url, formData, { ...config })
        .then(response => response.data)
        .catch(err => {
            return err
        })
}

const errors = (e) => {
    var message = ''
    console.log('e', e)
    if (e.response && e.response.status) {
        switch (e.response.status) {
            case 404:
                message = "No se encuentra la pagina que buscas"
                break
            case 422:
                message = getError(e)
                break
            case 500:
                message = "Sorry! something went wrong, please contact our support team";
                break
            case 401:
                message = "Invalid credentials"
                localStorage.removeItem("authUser")
                window.location.replace('/login')
                break
            default:
                message = e.response?.data?.message || e.message
                break
        }
    } else {
        message = e.message
    }
    ToastHelper(message, 'error')
    return false;
}


const getError = err => {
    const message = 'Invalido';
    const data = err.response?.data
    if (data?.errors) {
        if (typeof data?.errors === 'object')
            return Object.values(data?.errors).join(', ')
        if (typeof data?.errors === 'object')
            return data?.errors.join(', ')
    }
    if (data?.message)
        return data?.message
    if (data?.error)
        return data?.error
    return message;
}